import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, useTheme, useMediaQuery, Avatar, Typography, CircularProgress, Radio } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@mui/icons-material/Check';
import StarHeader from '../../../../../../../public/assets/icons/star_header.png';
import { useAppDispatch } from '@application/store/useStore';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { assignTicketFromDatabaseAsync, getAllTicketsAsync } from '@application/features/tickets/ticketsThunk';
import { toast } from 'react-toastify';
import { PrimaryGrid } from '@ui/components/common/Grid';
import NoDataMessage from '@ui/components/common/NoDataMessage';
import { getSuggestedPeopleForPartnerAsync } from '@application/features/partner/partnerThunk';
const BORDER = '1px solid rgba(145,158,171,0.35)';
const AssignTicketFromSuggestionDialog = ({ open, onClose, suggestion }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const dispatch = useAppDispatch();
    const { id: partnerId, eventId } = useParams();
    const { tickets } = useSelector((state) => state.tickets);
    const [selectedTicketId, setSelectedTicketId] = useState(null);
    const [isAssigning, setIsAssigning] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    // Fetch tickets with server-side pagination
    useEffect(() => {
        if (open && eventId) {
            const request = {
                eventId: eventId,
                pageNumber: paginationModel.page + 1,
                pageSize: paginationModel.pageSize,
                searchString: null
            };
            dispatch(getAllTicketsAsync(request));
        }
    }, [dispatch, eventId, open, paginationModel]);
    // Reset selected ticket when dialog closes
    useEffect(() => {
        if (!open) {
            setSelectedTicketId(null);
            setIsAssigning(false);
        }
    }, [open]);
    const getInitials = (name) => {
        return name
            .split(' ')
            .filter(Boolean)
            .map((n) => n[0].toUpperCase())
            .slice(0, 2)
            .join('');
    };
    // Get all tickets from current page
    const allTickets = useMemo(() => {
        return tickets?.items || tickets?.tickets || [];
    }, [tickets]);
    // Map all tickets to rows
    const ticketRows = useMemo(() => {
        return allTickets.map((ticket) => ({
            id: ticket.id,
            ticketNo: ticket.ticketNo,
            ticketType: ticket.ticketType,
            venueSection: ticket.venueSection,
            venueSectionRow: ticket.venueSectionRow,
            venueSectionSeat: ticket.venueSectionSeat,
            barcode: ticket.barcode,
            link: ticket.ticketUrl,
            assignedTo: ticket.assignedTo
        }));
    }, [allTickets]);
    const handleTicketSelection = useCallback((ticketId, isAssigned) => {
        if (!isAssigned) {
            setSelectedTicketId(ticketId);
        }
    }, []);
    const handleAssign = async () => {
        if (!suggestion || !selectedTicketId) {
            toast.error(t('sponsorships.tickets.assign.error.noSelection') || 'Please select a ticket');
            return;
        }
        setIsAssigning(true);
        const type = suggestion.isExternalRelation ? 'External' : 'Internal';
        try {
            const request = {
                ticketId: selectedTicketId,
                userId: suggestion.personId,
                type: type
            };
            const response = await dispatch(assignTicketFromDatabaseAsync(request)).unwrap();
            if (response.isSuccess) {
                toast.success(response.message ||
                    t('sponsorships.tickets.assign.success') ||
                    `Ticket successfully assigned to ${suggestion.name}`);
                // // Refresh tickets list
                // if (eventId) {
                //   const refreshRequest: GetAllTicketsRequest = {
                //     eventId: eventId,
                //     pageNumber: paginationModel.page + 1,
                //     pageSize: paginationModel.pageSize,
                //     searchString: null
                //   };
                //   dispatch(getAllTicketsAsync(refreshRequest));
                // }
                if (partnerId) {
                    dispatch(getSuggestedPeopleForPartnerAsync({
                        partnerId: partnerId,
                        eventId: eventId,
                        skip: 0,
                        take: null,
                        includePeople: true,
                        includeExternalRelations: true
                    }));
                }
                onClose();
            }
            else {
                toast.error(response.message ||
                    t('sponsorships.tickets.assign.error.failed') ||
                    'Failed to assign ticket');
            }
        }
        catch (error) {
            toast.error(t('sponsorships.tickets.assign.error.failed') || 'Failed to assign ticket');
        }
        finally {
            setIsAssigning(false);
        }
    };
    const columns = useMemo(() => [
        {
            field: 'select',
            headerName: '',
            width: 50,
            sortable: false,
            renderCell: (params) => {
                const isAssigned = !!params.row.assignedTo;
                return (_jsx(Radio, { checked: selectedTicketId === params.row.id, onChange: () => handleTicketSelection(params.row.id, isAssigned), disabled: isAssigned, size: 'small' }));
            }
        },
        {
            field: 'ticketNo',
            headerName: t('sponsorships.tickets.ticketNo') || 'Ticket No',
            flex: 1,
            minWidth: 110,
            renderCell: (params) => {
                const isAssigned = !!params.row.assignedTo;
                return (_jsx(Typography, { variant: 'body2', color: isAssigned ? 'text.disabled' : 'text.primary', sx: { opacity: isAssigned ? 0.6 : 1 }, children: params.row.ticketNo }));
            }
        },
        {
            field: 'ticketType',
            headerName: t('sponsorships.tickets.ticketType') || 'Ticket Type',
            flex: 1,
            minWidth: 120,
            renderCell: (params) => {
                const isAssigned = !!params.row.assignedTo;
                return (_jsx(Typography, { variant: 'body2', color: isAssigned ? 'text.disabled' : 'text.primary', sx: { opacity: isAssigned ? 0.6 : 1 }, children: params.row.ticketType || '-' }));
            }
        },
        {
            field: 'venueSection',
            headerName: t('sponsorships.tickets.ticketStand') || 'Ticket Stand',
            flex: 1,
            minWidth: 120,
            renderCell: (params) => {
                const isAssigned = !!params.row.assignedTo;
                return (_jsx(Typography, { variant: 'body2', color: isAssigned ? 'text.disabled' : 'text.primary', sx: { opacity: isAssigned ? 0.6 : 1 }, children: params.row.venueSection || '-' }));
            }
        },
        {
            field: 'venueSectionRow',
            headerName: t('sponsorships.tickets.ticketRow') || 'Ticket Row',
            flex: 1,
            minWidth: 100,
            renderCell: (params) => {
                const isAssigned = !!params.row.assignedTo;
                return (_jsx(Typography, { variant: 'body2', color: isAssigned ? 'text.disabled' : 'text.primary', sx: { opacity: isAssigned ? 0.6 : 1 }, children: params.row.venueSectionRow || '-' }));
            }
        },
        {
            field: 'venueSectionSeat',
            headerName: t('sponsorships.tickets.ticketSeatNumber') || 'Ticket Seat Number',
            flex: 1,
            minWidth: 150,
            renderCell: (params) => {
                const isAssigned = !!params.row.assignedTo;
                return (_jsx(Typography, { variant: 'body2', color: isAssigned ? 'text.disabled' : 'text.primary', sx: { opacity: isAssigned ? 0.6 : 1 }, children: params.row.venueSectionSeat || '-' }));
            }
        },
        {
            field: 'link',
            headerName: t('sponsorships.tickets.ticketLink') || 'Ticket Link',
            flex: 1,
            minWidth: 100,
            renderCell: (params) => {
                const isAssigned = !!params.row.assignedTo;
                return (_jsx(Box, { display: 'flex', alignItems: 'center', gap: 1, children: _jsx(Typography, { variant: 'body2', color: isAssigned ? 'text.disabled' : 'text.primary', sx: {
                            opacity: isAssigned ? 0.6 : 1,
                            textDecoration: 'underline',
                            cursor: isAssigned ? 'not-allowed' : 'pointer'
                        }, component: 'a', href: isAssigned ? undefined : params.row.link, target: isAssigned ? undefined : '_blank', rel: isAssigned ? undefined : 'noopener noreferrer', onClick: (e) => {
                            if (isAssigned) {
                                e.preventDefault();
                            }
                        }, children: params.row.link ? 'View Link' : '-' }) }));
            }
        },
        {
            field: 'barcode',
            headerName: t('sponsorships.tickets.ticketBarcode') || 'Ticket Barcode',
            flex: 1,
            minWidth: 110,
            renderCell: (params) => {
                const isAssigned = !!params.row.assignedTo;
                return (_jsx(Typography, { variant: 'body2', color: isAssigned ? 'text.disabled' : 'text.primary', sx: { opacity: isAssigned ? 0.6 : 1, fontFamily: 'monospace' }, children: params.row.barcode || '-' }));
            }
        },
        {
            field: 'assignedTo',
            headerName: t('sponsorships.tickets.columnAssignedTo') || 'Assigned To',
            flex: 1,
            minWidth: 130,
            renderCell: (params) => {
                if (params.row.assignedTo) {
                    return (_jsx(Typography, { variant: 'body2', color: 'text.secondary', children: params.row.assignedTo }));
                }
                return (_jsx(Typography, { variant: 'body2', color: 'text.disabled', children: t('sponsorships.tickets.unassigned') || 'Unassigned' }));
            }
        }
    ], [selectedTicketId, handleTicketSelection, t]);
    if (!suggestion)
        return null;
    return (_jsxs(Dialog, { open: open, onClose: onClose, maxWidth: 'lg', fullWidth: true, children: [_jsxs(DialogTitle, { sx: { textAlign: 'center', pt: 1 }, children: [_jsx(Box, { component: 'img', src: StarHeader, alt: 'star header', sx: { width: 48, height: 48, display: 'block', mx: 'auto', mb: 1 } }), t('sponsorships.tickets.assign.assignTickets') || 'Assign Tickets'] }), _jsxs(DialogContent, { children: [_jsxs(Box, { sx: {
                            p: 2.5,
                            mb: 3,
                            borderRadius: 2,
                            bgcolor: theme.palette.background.paper,
                            border: BORDER,
                            boxShadow: theme.customShadows.customCard
                        }, children: [_jsxs(Box, { display: 'flex', gap: 2, mb: 2, children: [_jsx(Avatar, { src: suggestion.profilePictureUrl, variant: 'rounded', sx: { width: 56, height: 56, fontSize: '18px' }, children: !suggestion.profilePictureUrl && getInitials(suggestion.name) }), _jsxs(Box, { flex: 1, minWidth: 0, children: [_jsx(Typography, { variant: 'h6', color: 'text.primary', sx: { fontWeight: 500, mb: 0.5 }, children: suggestion.name }), suggestion.title && (_jsx(Typography, { variant: 'body2', color: 'text.secondary', sx: { mb: 0.5 }, children: suggestion.title })), suggestion.email && (_jsx(Typography, { variant: 'body2', color: 'text.secondary', sx: { mb: 0.5 }, children: suggestion.email })), suggestion.companyName && (_jsx(Typography, { variant: 'body2', color: 'text.secondary', children: suggestion.companyName }))] })] }), suggestion.previousActivity?.description && (_jsx(Typography, { variant: 'body2', color: 'text.secondary', children: suggestion.previousActivity.description }))] }), _jsxs(Box, { children: [_jsx(Typography, { variant: 'h6', color: 'text.primary', sx: { mb: 2 }, children: t('sponsorships.tickets.selectTicket') || 'Select a Ticket to Assign' }), ticketRows.length === 0 ? (_jsx(NoDataMessage, { text: t('sponsorships.tickets.noTickets') || 'No tickets available' })) : (_jsx(PrimaryGrid, { rows: ticketRows, columns: columns, maxHeight: '400px', showFilters: false, showHeaderControls: false, showNewHeaderControls: true, paginationMode: 'server', paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, rowCount: tickets?.totalCount || 0, pageSizeOptions: [5, 10, 25, 50], disableRowSelectionOnClick: true, getRowClassName: (params) => (params.row.assignedTo ? 'disabled-row' : ''), sx: {
                                    '& .disabled-row': {
                                        opacity: 0.6,
                                        cursor: 'not-allowed',
                                        '&:hover': {
                                            backgroundColor: 'transparent'
                                        }
                                    }
                                } }))] })] }), _jsx(DialogActions, { sx: { px: 3, pb: 2 }, children: _jsxs(Box, { sx: {
                        display: 'flex',
                        gap: isMobile ? 1 : 2,
                        width: '100%',
                        flexDirection: isMobile ? 'column' : 'row'
                    }, children: [_jsx(Button, { variant: 'outlined', fullWidth: true, onClick: onClose, sx: { borderRadius: '12px' }, disabled: isAssigning, children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', fullWidth: true, startIcon: isAssigning ? _jsx(CircularProgress, { size: 16, color: 'inherit' }) : _jsx(CheckIcon, {}), onClick: handleAssign, disabled: !selectedTicketId || isAssigning, sx: {
                                borderRadius: '12px',
                                backgroundColor: (theme) => theme.palette.grey[800],
                                color: '#fff',
                                '&:hover': { backgroundColor: (theme) => theme.palette.grey[900] }
                            }, children: isAssigning
                                ? t('sponsorships.tickets.assigning') || 'Assigning...'
                                : t('sponsorships.tickets.assignTicket') || 'Assign Ticket' })] }) })] }));
};
export default AssignTicketFromSuggestionDialog;
